/**
 * TimeCompare 组件
 * 用于时间对比分析的受控组件
 */

import React, {useEffect, useState, useRef} from 'react';
import {useIntl} from "umi";
import classNames from 'classnames';
import {Switch, Radio} from 'tea-component';
import {ITimeCompareProps} from './types';
import RelativeTimeRangeSelector from '../RelativeTimeRangeSelector';
import IconButtonWithBubble from "@/pages/datalab/components/IconButtonWithBubble";
import DatalabGrid from "@/pages/datalab/components/DatalabGrid";
import './index.scss';
import {DateShowType, TRelativeTime} from "@dbl/core/date";
import { getPreviousPeriodValue } from '@dbl/core/date';
import RelativeDatePicker from "@/pages/datalab/components/DateComponents/RelativeDatePicker";
import {joinDate} from "@dbl/core/date";
import ConfirmAndCancel from "@/pages/datalab/components/ConfirmAndCancle";
import CommonBubble from '@home/components/CommonBubble';
import { LANGUAGE_EN } from '@home/_config/config';

const TimeCompare: React.FC<ITimeCompareProps> = ({
  disabled,
  defaultCompareTimes,
  onConfirm,
  onCancel,
  baseTimeRange,
  baseTimeName,
  timeGranularity,
  // info,
  className = '',
  onStateChange,
  mountClassNames,
  type,
  openSingle,
  showBaseTime=true,
  dropdownProps,
  popoverProps,
}) => {
  const { locale, formatMessage } = useIntl();
  const isEn = locale === LANGUAGE_EN; // 是否是英文
  // 开关状态
  const [isOpen, setIsOpen] = useState<boolean>(defaultCompareTimes?.length > 0);

  // 对比时间项状态
  const [compareTimes, setCompareTimes] = useState<TRelativeTime[]>(defaultCompareTimes || []);
  const baseTimeRef = useRef<HTMLSpanElement | null>(null);
  const [isBaseTimeOverflow, setIsBaseTimeOverflow] = useState(false);
  
  const [radioIndex, setRadioIndex] = useState(() => {
    let radioIndex = 0;
    if (openSingle) {
      // 开启单选状态后, 从对比日期中找到 被选中的日期.
      radioIndex = defaultCompareTimes.findIndex(time => time.is_active);
    }
    return radioIndex === -1 ? 0 : radioIndex;
  });
  
  useEffect(() => {
    setIsOpen(defaultCompareTimes?.length > 0);
    setCompareTimes(defaultCompareTimes || []);
  }, [defaultCompareTimes]);
  
  useEffect(() => {
    onStateChange?.(isOpen, compareTimes);
  }, [isOpen, compareTimes]);

  const baseTimeText = `${baseTimeName || ''} (${joinDate(baseTimeRange, timeGranularity)})`;

  useEffect(() => {
    if (!baseTimeRef.current) return;
    const el = baseTimeRef.current;
    // 元素真实内容宽度大于可视宽度时，认为发生了溢出（显示 ...）
    const isOverflow = el.scrollWidth > el.clientWidth;
    setIsBaseTimeOverflow(isOverflow);
  }, [baseTimeName, baseTimeRange, timeGranularity]);
  
  // 处理开关变化
  const handleSwitchChange = (checked: boolean): void => {
    setIsOpen(checked);
    if (compareTimes?.length > 0) return;
    // 如果没有对比项时间, 则新增一个
    const value = getPreviousPeriodValue(baseTimeRange, timeGranularity);
    setCompareTimes([value]);
  };

  // 处理对比时间项变化
  const handleCompareTimeChange = (index: number, value?: TRelativeTime): void => {
    const newCompareTimeItems = [...compareTimes];
    
    if (value) {
      newCompareTimeItems[index] = value;
    } else {
      // 移除该项
      newCompareTimeItems.splice(index, 1);
      // 单选模式下 删除了选中的对比时间, 重置第一个对比时间为选中态
      if (openSingle && index === radioIndex) {
        setRadioIndex(0);
      }
    }
    
    setCompareTimes(newCompareTimeItems);
  };

  // 添加新的对比时间项
  const handleAddCompareTime = (): void => {
    const value = getPreviousPeriodValue(baseTimeRange, timeGranularity);
    setCompareTimes([...compareTimes, value]);
  };

  // 处理确定按钮点击
  const handleConfirm = (): void => {
    
    if (!isOpen) { // 关闭传空数组
      onConfirm([]);
      return;
    }
    if (openSingle) { // 单选状态
      const newCompareTimeItems = compareTimes.map((time, index) => {
        // 给新选中的对比日期赋值true, 给旧选中的对比日期重置为false.
        time.is_active = index === radioIndex;
        return time;
      });
      onConfirm(newCompareTimeItems);
      return;
    }
    onConfirm([...compareTimes]);
  };

  // 处理取消按钮点击
  const handleCancel = (): void => {
    onCancel();
  };
  
  const RelativeDate = type === DateShowType.DYNAMIC ? RelativeTimeRangeSelector : RelativeDatePicker;
  return (
    <div className={`dbl-time-compare ${className}`}>
      {/* 统一的网格布局 */}
      {
        disabled ? '' : (<DatalabGrid
          colCount={compareTimes?.length > 1 ? 4 : 3}
          gap="20px"
          columnWidthMode={'max-content'}
        >
          <DatalabGrid.Item className="dbl-time-compare__switch-section">
            {formatMessage({id: 'dbl_time_compare_title'})}
          </DatalabGrid.Item>
          <Switch
            value={isOpen}
            onChange={handleSwitchChange}
            dblGridConfig={{
              colSpan: compareTimes?.length > 1 ? 3 : 2
            }}
          />
          
          {
            (isOpen && showBaseTime) ? (<DatalabGrid.Item
              className="dbl-time-compare__base-time"
              colSpan={compareTimes?.length > 1 ? 4 : 3}
            >
              <span className={'dbl-time-compare__base-time-label'}>{formatMessage({id: 'dbl_time_compare_base'})}:</span>
              <CommonBubble 
                content={isBaseTimeOverflow ? baseTimeText : ''}
                overlayStyle={{
                  zIndex: 999999999,
                }}
              >
                <span
                  ref={baseTimeRef}
                  className={'dbl-time-compare__base-time-value'}
                >
                  {baseTimeText}
                </span>
              </CommonBubble>
            </DatalabGrid.Item>) : ''
          }
          
          
          {/* 对比时间选择区域 */}
          {isOpen && compareTimes.map((compareTime, index) => (
            <React.Fragment key={index}>
              <DatalabGrid.Item className={classNames('dbl-time-compare-title', { 'dbl-time-compare-title-en': isEn })}>
                {
                  openSingle ? <Radio value={index === radioIndex} onClick={() => setRadioIndex(index)}> {formatMessage({id: 'dbl_time_compare_label'})} {index + 1} </Radio>: `${formatMessage({id: 'dbl_time_compare_label'})} ${index + 1}`
                }
              </DatalabGrid.Item>
              
              <RelativeDate
                baseTimeRange={baseTimeRange}
                timeGranularity={timeGranularity}
                value={compareTime}
                onChange={(value) => handleCompareTimeChange(index, value)}
                mountClassNames={mountClassNames}
                dropdownProps={dropdownProps}
                popoverProps={popoverProps}
              />
              
              <IconButtonWithBubble
                bubbleConfig={{content: compareTimes?.length === 4 ? formatMessage({id: 'dbl_time_compare_add_disabled'}) : formatMessage({id: 'dbl_common_add'}), overlayClassName: 'dbl-time-compare__add'}}
                iconConfig={{
                  size: 24,
                  type: 'add-icon-type',
                  isSvg: true,
                  onClick: handleAddCompareTime,
                  disabled: compareTimes?.length === 4,
                }}
              />
              
              {compareTimes?.length > 1 && (
                <IconButtonWithBubble
                  bubbleConfig={{content: formatMessage({id: 'dbl_common_delete'}), overlayClassName: 'dbl-time-compare__delete'}}
                  iconConfig={{
                    size: 24,
                    type: 'trash-can-icon',
                    isSvg: true,
                    className: 'dbl-time-compare-delete',
                    onClick: () => handleCompareTimeChange(index)
                  }}
                />
              )}
            </React.Fragment>
          ))}
        </DatalabGrid>)
      }
      {/* 操作按钮区域 */}
      <ConfirmAndCancel
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        bottomPadding={0}
      />
    </div>
  );
};

TimeCompare.displayName = 'TimeCompare';

export default TimeCompare;
